import { GoFeralAuthClient } from '../../clients/GoFeralAuthClient';
import { action, computed, observable } from 'mobx';
import { Session, SessionSerialized } from './Session';
import { v4 } from 'uuid';
import { NotificationStore } from '../NotificationStore';
import { ObjectId } from 'bson';

export interface SessionStoreOptions {
  auth_client: GoFeralAuthClient;
  notification_store: NotificationStore;
}

export interface SessionStoreSerialized {
  sessions: SessionSerialized[];
  current_session: string;
}

export class SessionStore {
  static STORAGE_KEY = 'SESSION_STORE';

  @observable
  protected accessor _sessions: Set<Session>;

  @observable
  accessor current_session: Session;

  constructor(protected options: SessionStoreOptions) {
    this.current_session = null;
    this._sessions = new Set<Session>();
  }

  @computed get sessions() {
    return Array.from(this._sessions.values());
  }

  init() {
    let res = window.localStorage.getItem(SessionStore.STORAGE_KEY);
    if (res) {
      this.deserialize(JSON.parse(res));
    }
  }

  logout() {
    this._sessions.forEach((s) => {
      s.deactivate();
      s.dispose();
    });
    this._sessions.clear();
    this.current_session = null;
    window.localStorage.removeItem(SessionStore.STORAGE_KEY);
  }

  save() {
    window.localStorage.setItem(SessionStore.STORAGE_KEY, JSON.stringify(this.serialize()));
  }

  @action
  async login(options: { email: string; password: string }) {
    let res = await this.options.auth_client.login({
      email: options.email,
      password: options.password
    });

    const sessions = res.profiles.map((r) => {
      let session = this.generateSession(r);
      session.deserialize({
        refresh_token: res.token,
        profile: null,
        session_id: v4(),
        profile_id: r.toString()
      });
      return session;
    });

    for (let session of sessions) {
      this._sessions.add(session);
      await session.user.refresh();

      // activate the first session
      if (!this.current_session) {
        session.activate();
      }
    }

    this.save();
  }

  protected generateSession(profile_id: ObjectId) {
    const session = new Session({
      auth_client: this.options.auth_client,
      notification_store: this.options.notification_store,
      profile_id: profile_id
    });

    let l1 = session.registerListener({
      activate: () => {
        this.current_session?.deactivate();
        this.current_session = session;
      },
      logout: () => {
        this.logout();
      },
      dispose: () => {
        l1();
      },
      refreshTokenUpdated: () => {
        this.save();
      }
    });
    return session;
  }

  @action protected deserialize(data: SessionStoreSerialized) {
    try {
      let failed = false;
      const sessions = data.sessions.map((s) => {
        const session = this.generateSession(new ObjectId(s.profile_id));
        if (!session.deserialize(s)) {
          failed = true;
        }
        return session;
      });
      if (failed) {
        return;
      }
      this._sessions = new Set(sessions);
      sessions.find((s) => s.id === data.current_session)?.activate();
      sessions.forEach((s) => {
        s.user?.refresh();
      });
    } catch (ex) {
      window.localStorage.clear();
      throw ex;
    }
  }

  protected serialize(): SessionStoreSerialized {
    return {
      current_session: this.current_session?.id,
      sessions: this.sessions.map((s) => s.serialize())
    };
  }
}
